/** Which of the client-side payload caches are on; the choice outlives the tab. */
export interface ClientCacheSettings {
  /** `HsSceneSource.loadSettings.useCache`: meshes and lines in IndexedDB. */
  geometry: boolean;
  /** `HsHlodPayloadCache.enabled`: photogrammetry manifests, subtrees and node payloads in IndexedDB. */
  photogrammetry: boolean;
}

export const DEFAULT_CLIENT_CACHE_SETTINGS: ClientCacheSettings = { geometry: true, photogrammetry: true };

const STORAGE_KEY: string = 'hyperscene.demo.clientCaches';

/** Reads the switches from local storage; missing or unreadable values fall back to the defaults (both on). */
export function readClientCacheSettings(storage: Storage | null = browserStorage()): ClientCacheSettings {
  try {
    const raw: string | null = storage?.getItem(STORAGE_KEY) ?? null;
    if (raw === null) {
      return { ...DEFAULT_CLIENT_CACHE_SETTINGS };
    }
    const parsed: Partial<ClientCacheSettings> = JSON.parse(raw) as Partial<ClientCacheSettings>;
    return {
      geometry: typeof parsed.geometry === 'boolean' ? parsed.geometry : DEFAULT_CLIENT_CACHE_SETTINGS.geometry,
      photogrammetry:
        typeof parsed.photogrammetry === 'boolean'
          ? parsed.photogrammetry
          : DEFAULT_CLIENT_CACHE_SETTINGS.photogrammetry,
    };
  } catch {
    return { ...DEFAULT_CLIENT_CACHE_SETTINGS };
  }
}

/** Stores the switches; a storage that is missing or full is ignored, the session keeps the in-memory values. */
export function writeClientCacheSettings(
  settings: ClientCacheSettings,
  storage: Storage | null = browserStorage()
): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Private mode or a full storage: the switch still applies to this tab.
  }
}

function browserStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
